import { HttpClient, QueryParams } from "../httpClient";

/**
 * Available export fields for the experts export endpoint.
 * These match the API's ExpertExportField enum.
 */
export const EXPERT_EXPORT_FIELDS = {
    expert_name: 'Expert Name',
    expert_type: 'Expert Type',
    user_name: 'User Name',
    email: 'Email',
    services: 'Services',
    subservices: 'Subservices',
    reviews_count: 'Number of Reviews',
    partner_badges: 'Partner Badges',
} as const;

export type ExpertExportField = keyof typeof EXPERT_EXPORT_FIELDS;

export interface ExportExpertsQueryParams extends QueryParams {
    fields: string;
}

export interface ExportExpertsResult {
    blob: Blob;
    filename: string;
}

/**
 * Creates the experimental admin experts export API.
 * This API returns a CSV file download rather than JSON.
 */
export function createExperimentalAdminExpertsExportApi(http: HttpClient, baseUrl: string) {
    const experimentalAdminBaseUrl = `${baseUrl}/experimental/admin/experts`;
    
    return {
        /**
         * Export experts to CSV with selected fields.
         * Returns a blob and suggested filename for download.
         * 
         * @param fields - Array of field names to include in the export
         * @returns Promise resolving to blob and filename
         */
        exportToCsv: async (fields: ExpertExportField[]): Promise<ExportExpertsResult> => {
            const url = `${experimentalAdminBaseUrl}/export?fields=${fields.join(',')}`;
            
            // Make the request using the http client
            // Since this returns a CSV file, we need special handling
            const response = await http.get<Blob>(url, {
                headers: {
                    'Accept': 'text/csv',
                },
            });
            
            // Extract filename from Content-Disposition header if available
            const contentDisposition = response.headers['content-disposition'];
            let filename = `experts-export-${new Date().toISOString().slice(0, 10)}.csv`;
            
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="?([^";\n]+)"?/);
                if (filenameMatch && filenameMatch[1]) {
                    filename = filenameMatch[1];
                }
            }
            
            return {
                blob: response.data,
                filename,
            };
        },
        
        /**
         * Get available export fields with their labels.
         * This is a client-side helper, not an API call.
         */
        getAvailableFields: (): Record<ExpertExportField, string> => {
            return { ...EXPERT_EXPORT_FIELDS };
        },
    };
}
